import { useMemo } from 'react'
import { Colors } from 'design/colors'
import { FaultCode, ControllerState } from 'utils/common-types'
import { camelCaseToReadable, controllerStateToString, faultCodeToString } from 'utils/common'
import { TelemValue } from 'cstore/telem'
import { useSingleTelemetryValue } from 'hooks/useSingleTelemetryValue'
import { REFRESH_MS } from 'hooks/usePlayback'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGripVertical, faXmark } from '@fortawesome/free-solid-svg-icons'
import { PlotKey } from 'utils/packet.worker'
import { displayUnit, plotKeyToMultiMotorInfo } from 'utils/packet-workers'
import { actuatorDispositionStyle } from 'design/colors'
import { useDevices } from 'hooks/useDevices'
import { TagsToml } from 'cstore/cstore'
import { tags } from 'cstore/toml/toml'

export interface IGaugeWidgetProps {
  close: () => void
}

export interface IGaugeProps {
  packetKey: PlotKey
}

const MOTOR_TEMPERATURE_KEYS = new Set(['motorTemperatureC', 'mosfetTemperature'])
const INVERTER_TEMPERATURE_KEYS = new Set(['inverterTemperatureC'])

export const Gauge = (props: IGaugeProps & IGaugeWidgetProps): JSX.Element => {
  const latestValue: TelemValue | undefined = useSingleTelemetryValue(props.packetKey, REFRESH_MS)
  const { connectedDevice } = useDevices()

  const cstore = connectedDevice?.initialized ? connectedDevice.cstore : undefined
  const temperatureThresholds = useMemo(() => {
    let motorOvertemperatureThreshold: number | undefined
    let motorOvertemperatureRecoveryDelta: number | undefined
    let overtemperatureThreshold: number | undefined
    if (cstore) {
      for (let i = 0; i < cstore.storedTags.length; i++) {
        const tag = cstore.storedTags[i]
        const def = tag.getTagDefinition(tags as TagsToml)
        if (def?.name === 'PP_MotorOvertemperatureThreshold') {
          const value = tag.getData() as number
          if (isFinite(value)) motorOvertemperatureThreshold = value
        } else if (def?.name === 'PP_MotorOverTempRecoveryDelta_C') {
          const value = tag.getData() as number
          if (isFinite(value)) motorOvertemperatureRecoveryDelta = value
        } else if (def?.name === 'PP_OvertemperatureThreshold') {
          const value = tag.getData() as number
          if (isFinite(value)) overtemperatureThreshold = value
        }
      }
    }
    return {
      motorOvertemperatureThreshold,
      motorOvertemperatureRecoveryDelta,
      overtemperatureThreshold
    }
  }, [cstore])

  if (latestValue === undefined) return <></>

  let text = '---'
  if (typeof latestValue === 'number') {
    text = `${latestValue.toFixed(3)} ${displayUnit(props.packetKey)}`
  } else if (typeof latestValue === 'boolean') {
    text = latestValue.toString()
  }
  if (props.packetKey === 'controllerState' && typeof latestValue !== 'boolean') {
    if (!isFinite(latestValue) || !(latestValue in ControllerState)) {
      // ToDo: I wish there was a way of also swapping the state when Gauges get swapped by drag-and-drop
      text = '---'
    } else {
      let controllerState: ControllerState = latestValue
      text = controllerStateToString(controllerState)
    }
  } else if (props.packetKey === 'faultCodeDeprecated' && typeof latestValue !== 'boolean') {
    // TODO: remove this block after a few months of FaultControl widget in prod
    if (!isFinite(latestValue) || !(latestValue in FaultCode)) {
      // ToDo: I wish there was a way of also swapping the state when Gauges get swapped by drag-and-drop
      text = '---'
    } else {
      let faultCode: FaultCode = latestValue
      text = faultCodeToString(faultCode)
    }
  }

  const baseName = plotKeyToMultiMotorInfo(props.packetKey)?.baseName ?? props.packetKey
  const isMotorTemperatureKey = MOTOR_TEMPERATURE_KEYS.has(baseName)
  const isInverterTemperatureKey = INVERTER_TEMPERATURE_KEYS.has(baseName)

  const {
    motorOvertemperatureThreshold,
    motorOvertemperatureRecoveryDelta,
    overtemperatureThreshold
  } = temperatureThresholds

  let displayColor = Colors.WHITE
  if (
    isMotorTemperatureKey &&
    typeof latestValue === 'number' &&
    motorOvertemperatureThreshold !== undefined &&
    motorOvertemperatureRecoveryDelta !== undefined
  ) {
    if (latestValue >= motorOvertemperatureThreshold) {
      displayColor = Colors.RED
    } else if (latestValue >= motorOvertemperatureThreshold - motorOvertemperatureRecoveryDelta) {
      displayColor = Colors.ORANGE
    } else {
      displayColor = Colors.LIGHT_LIGHT_GREEN
    }
  } else if (
    isInverterTemperatureKey &&
    typeof latestValue === 'number' &&
    overtemperatureThreshold !== undefined
  ) {
    if (latestValue >= overtemperatureThreshold) {
      displayColor = Colors.RED
    } else {
      displayColor = Colors.LIGHT_LIGHT_GREEN
    }
  }

  const multiMotorInfo = plotKeyToMultiMotorInfo(props.packetKey)
  const multiMotorLabel = multiMotorInfo ? (
    <span style={actuatorDispositionStyle}>{multiMotorInfo.label}</span>
  ) : null
  return (
    <div style={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <div style={{ display: 'flex', flex: 'column' }}>
          {/* Drag and drop drag handle */}
          <FontAwesomeIcon
            icon={faGripVertical}
            color={Colors.WHITE}
            style={{ padding: '0px 8px 0px 0px', cursor: 'pointer' }}
            size="lg"
          />
          <div style={{ color: Colors.WHITE }}>
            {multiMotorLabel} {camelCaseToReadable(baseName)}
          </div>
        </div>
        <FontAwesomeIcon
          icon={faXmark}
          color={Colors.WHITE}
          style={{ padding: '0px 4px', cursor: 'pointer' }}
          onClick={props.close}
          size="lg"
        />
      </div>
      <div
        style={{
          display: 'flex',
          flex: 1,
          fontSize: 48,
          color: displayColor,
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        {text}
      </div>
    </div>
  )
}
